import React from 'react';

const PARTICLES = Array.from({ length: 40 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 2 + 1,
  opacity: Math.random() * 0.35 + 0.08,
  delay: Math.random() * 4,
  duration: Math.random() * 6 + 8,
}));

const Hero = () => {
  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '100+', label: 'Projects Delivered' },
    { value: '50+',  label: 'Happy Clients' },
    { value: '3+',   label: 'Years of Excellence' },
    { value: '24/7', label: 'Support Available' },
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-brand-bg scroll-mt-16">
      {/* Brand film background */}
      <div className="absolute inset-0 pointer-events-none">
        <video
          className="hero-video"
          src="/assets/avarnix.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="absolute inset-0 hero-video-scrim" />
        <div className="absolute inset-0 hero-video-vignette" />
      </div>

      {/* Grid */}
      <div className="absolute inset-0 grid-bg opacity-35 pointer-events-none" />

      {/* Ambient glows — opacity is set via CSS var --glow-opacity in each theme */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px] pointer-events-none glow-blob"
        style={{ background: 'rgb(var(--color-blue))' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-[100px] pointer-events-none glow-blob animate-glow-pulse"
        style={{ background: 'rgb(var(--color-purple))', animationDelay: '1.5s' }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[140px] pointer-events-none"
        style={{ background: 'rgb(var(--color-cyan))', opacity: 'calc(var(--glow-opacity) * 0.4)' }}
      />

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {PARTICLES.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: 'rgb(var(--color-blue))',
              opacity: p.opacity,
              animation: `float ${p.duration}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 text-center">

        {/* Badge */}
        <div className="inline-flex items-center gap-2 hero-glass px-4 py-2 rounded-full text-sm font-medium text-white mb-8 animate-fade-in"
          style={{ borderColor: 'rgba(var(--color-blue), 0.25)' }}
        >
          <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
          AI-Powered Solutions · Bangalore, India
        </div>

        {/* Headline */}
        <h1 className="hero-heading font-black hero-video-title mb-6 leading-[1.05] tracking-tight animate-slide-up">
          Innovating the Future<br />
          with{' '}
          <span className="text-gradient">Artificial</span>
          <br />
          <span className="text-gradient">Intelligence</span>
        </h1>

        {/* Sub-headline */}
        <p className="text-lg md:text-xl hero-video-copy max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in" style={{ animationDelay: '0.3s' }}>
          Avarnix Technologies builds world-class AI products and custom software solutions that transform businesses.
          From intelligent chatbots to full-stack SaaS platforms — we engineer the future.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 md:mb-20 animate-fade-in" style={{ animationDelay: '0.5s' }}>
          <button onClick={() => scrollToSection('#products')} className="btn-primary text-base px-8 py-4 rounded-xl">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Explore Our Products
          </button>
          <button onClick={() => scrollToSection('#contact')} className="btn-secondary text-base px-8 py-4 rounded-xl">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Free Consultation
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto animate-fade-in" style={{ animationDelay: '0.7s' }}>
          {stats.map((stat, i) => (
            <div key={i} className="hero-stat-card hover:shadow-card-hover transition-all duration-300">
              <div className="text-3xl md:text-4xl font-black text-gradient mb-1">{stat.value}</div>
              <div className="text-sm text-white/75 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <button
          onClick={() => scrollToSection('#about')}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/60 hover:text-brand-blue transition-colors animate-bounce"
        >
          <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default Hero;
